/**
 * Wire shapes: the JSON the backends emit, and the schemas that parse it at the API boundary
 * (see docs/rules/api_boundary.md). The schemas of vms_database's answers are generated from its
 * openapi.yaml (`npm run generate`); the media_server's own surfaces are written here.
 */

import { z } from 'zod';

import { keyBy, listOf } from '@/api/listOf';
import { declareMediaServerDefault } from '@/api/mediaServerFill';

import type {
    BuildArtifact as BuildArtifactSchema,
    MaintenanceWindow as MaintenanceWindowSchema,
    ReleaseManifest as ReleaseManifestSchema,
    UpdateCandidate as UpdateCandidateSchema,
} from './generated/vms_database.zod';
import {
    AvailableUpdates as AvailableUpdatesSchema,
    Build as BuildSchema,
    ClaimResponse as GeneratedClaimResponseSchema,
    GetArchiveEncryptionResponse as ArchiveEncryptionStatusSchema,
    Location as LocationSchema,
    LocationWithServers as LocationWithServersSchema,
    ManifestStatus as ManifestStatusSchema,
    PendingResponse as GeneratedPendingResponseSchema,
    Server as GeneratedServerSchema,
    ServerReplacement as ServerReplacementSchema,
    ServerUpdateStatus as GeneratedServerUpdateStatusSchema,
    Storage as StorageSchema,
    UpdatePlan as UpdatePlanSchema,
} from './generated/vms_database.zod';
import { ResourceStatusSchema } from './notifications';
import type { SettingsSnapshot } from './snapshot';

// A node names its own kind, and the cloud stores whatever it sent.
export const ServerKindSchema = z.enum(['media_server', 'ai_agent_toolbox']).catch('media_server');
export type ServerKind = z.infer<typeof ServerKindSchema>;

export const ServerSchema = GeneratedServerSchema.extend({ kind: ServerKindSchema.optional() });
export type Server = z.infer<typeof ServerSchema>;

/** What a server replacement moved to the target. */
export { ServerReplacementSchema };
export type ServerReplacement = z.infer<typeof ServerReplacementSchema>;

/** Whether new archive is encrypted, and whether a key is saved to turn it back on. */
export { ArchiveEncryptionStatusSchema };
export type ArchiveEncryptionStatus = z.infer<typeof ArchiveEncryptionStatusSchema>;

/** The archive encryption state as fetched. */
export interface ArchiveEncryptionSnapshot {
    /** Null until a load answers. */
    status: ArchiveEncryptionStatus | null;
    isLoading: boolean;
    error: Error | null;
}

export { LocationSchema };
export type Location = z.infer<typeof LocationSchema>;

/**
 * How a location is read back: the entity plus the ids of the servers in it. Membership is not
 * stored on the location row, so the list and create answers carry it and a rename answers with
 * the entity alone.
 */
export { LocationWithServersSchema };
export type LocationWithServers = z.infer<typeof LocationWithServersSchema>;

/** The locations list as fetched. */
export interface LocationsSnapshot {
    locations: LocationWithServers[];
    isLoading: boolean;
    error: Error | null;
}

/** The servers list as fetched. */
export interface ServersSnapshot {
    servers: Server[];
    isLoading: boolean;
    /** True once a list has arrived. */
    isLoaded: boolean;
    error: Error | null;
}

/**
 * A server as the fleet lists it: the roster entity plus whether it is online
 * (from its status frames) and its latest update report.
 */
export interface FleetServer {
    id: string;
    name: string;
    kind?: ServerKind;
    locationId: string | null;
    isOnline?: boolean;
    updateStatus: ServerUpdateStatus | null;
}

/** One location of the Servers rail, and its servers. */
export interface ServerGroup {
    id: string;
    name: string;
    servers: FleetServer[];
}

/** What a pairing code resolves to: the node waiting to be claimed. */
export const PendingServerSchema = GeneratedPendingResponseSchema.extend({
    kind: ServerKindSchema.optional(),
});
export type PendingServer = z.infer<typeof PendingServerSchema>;

/** What claiming a pairing code answers with. */
export const ClaimedServerSchema = GeneratedClaimResponseSchema.extend({
    kind: ServerKindSchema.optional(),
});
export type ClaimedServer = z.infer<typeof ClaimedServerSchema>;

/** The one word the cloud derives from a server's update report. */
export type UpdateState = z.infer<typeof GeneratedServerUpdateStatusSchema>['state'];

// The cloud stores the phase a media_server reports unchecked, so a newer server's phase
// arrives as it was sent.
export const UpdatePhaseSchema = z.enum(['idle', 'downloaded', 'applying', 'unknown'])
    .catch('unknown');
export type UpdatePhase = z.infer<typeof UpdatePhaseSchema>;

/**
 * How a media_server's last update attempt ended. The cloud stores what the server sent, so a
 * newer server's outcome, or `""`, arrives as it is.
 */
export type LastOutcome =
    'none' | 'succeeded' | 'downloadFailed' | 'verificationFailed' | 'installFailed' |
    'unhealthy' | (string & NonNullable<unknown>);

export const LastOutcomeSchema = z.custom<LastOutcome>(
    (value: unknown): boolean => typeof value === 'string',
);

/**
 * What one media_server reports about its own updating, plus the state the cloud derives from
 * it. Stored per server, so an offline server still has a last known version; a server that
 * never reported is listed with Go's zero `reportedAt` and `unknown`.
 */
export const ServerUpdateStatusSchema = GeneratedServerUpdateStatusSchema.extend({
    phase: UpdatePhaseSchema,
    lastOutcome: LastOutcomeSchema,
});
export type ServerUpdateStatus = z.infer<typeof ServerUpdateStatusSchema>;

/** Server host clock and its IANA zone, which this server's timestamps render in. */
export const ServerTimeSchema = z.object({
    utcTimeMs: z.number(),
    timeZone: z.string(),
    timeZoneOffsetMinutes: z.number(),
    /** The server's wall clock with its offset, as Qt writes an ISO date. */
    localTime: z.iso.datetime({ offset: true }),
});
export type ServerTime = z.infer<typeof ServerTimeSchema>;

// Build registry ─────────────────────────────────────────────────────

export type BuildArtifact = z.infer<typeof BuildArtifactSchema>;

/** One registry row: a version this environment may offer. */
export { BuildSchema };
export type Build = z.infer<typeof BuildSchema>;

/** The newest build CI published. */
export type ReleaseManifest = z.infer<typeof ReleaseManifestSchema>;

/** The manifest card: the live manifest, or why it could not be read. */
export { ManifestStatusSchema };
export type ManifestStatus = z.infer<typeof ManifestStatusSchema>;

// Update plans ───────────────────────────────────────────────────────

/** A daily span, `HH:MM` in each server's own zone; `end` before `start` crosses midnight. */
export type MaintenanceWindow = z.infer<typeof MaintenanceWindowSchema>;

export const UpdatePlanState = {
    Planned: 'planned',
    Ready: 'ready',
    Installing: 'installing',
    Complete: 'complete',
    Cancelled: 'cancelled',
} as const;

export type UpdatePlanState = typeof UpdatePlanState[keyof typeof UpdatePlanState];

/**
 * The organization's decision to move to a build. Open while neither timestamp is set. The
 * create answer carries no `state`; every later answer does.
 */
export { UpdatePlanSchema };
export type UpdatePlan = z.infer<typeof UpdatePlanSchema>;

/**
 * The build the fleet could move to, while at least one of the servers the caller can reach is
 * behind it.
 */
export type UpdateCandidate = z.infer<typeof UpdateCandidateSchema>;

/** `/updates/available`: what the fleet is offered and what it runs now. */
export { AvailableUpdatesSchema };
export type AvailableUpdates = z.infer<typeof AvailableUpdatesSchema>;

export interface CreateUpdatePlanRequest {
    version: string;
    maintenanceWindow: MaintenanceWindow;
    installNow?: boolean;
}

export interface CreatePrivateUpdatePlanRequest {
    key: string;
    maintenanceWindow: MaintenanceWindow;
    installNow?: boolean;
}

// The media_server's own surfaces ─────────────────────────────────────
// These come from the server itself, not from vms_database: every one of
// them is forwarded through the relay tunnel by serverId, so
// they are readable only while that server is online.

/** The node's identity card. */
export const ServerInfoSchema = z.object({
    serverId: z.string(),
    serverName: z.string(),
    cloudUrl: z.string(),
    orgId: z.string(),
    version: z.string(),
    revision: z.string(),
    /** Windows alone takes share credentials; elsewhere the admin mounts the share. */
    platform: z.enum(['windows', 'macos', 'linux']),
});
export type ServerInfo = z.infer<typeof ServerInfoSchema>;

/** One NIC of the server, with the addresses it currently carries. */
export const NetworkInterfaceSchema = z.object({
    name: z.string(),
    displayName: z.string(),
    /** The server names every other kind `unknown`. */
    type: z.enum(['ethernet', 'wifi', 'virtual', 'ppp', 'loopback', 'tunnel', 'unknown']),
    ips: z.array(z.string()),
});
export type NetworkInterface = z.infer<typeof NetworkInterfaceSchema>;

/** The server's local addresses, flat and grouped by NIC. */
export const ServerLocalAddressesSchema = z.object({
    ips: z.array(z.string()),
    interfaces: listOf(NetworkInterfaceSchema, 'interfaces', { getKey: keyBy('name') }),
});
export type ServerLocalAddresses = z.infer<typeof ServerLocalAddressesSchema>;

/**
 * The server's NetworkInterfaceFilter: what is turned off, everything else being on. Its default
 * is `{}`, so a list it does not name is empty.
 */
const NetworkInterfaceFilterSchema = z.looseObject({
    /** Interfaces whose every address is off, by OS name. */
    disabledInterfaces: z.array(z.string()).default([]),
    disabledAddresses: z.array(z.string()).default([]),
});
export type NetworkInterfaceFilter = z.infer<typeof NetworkInterfaceFilterSchema>;

// The filter is a string property on the server, so it travels as a JSON document in a string.
const NetworkInterfaceFilterCodec = z.codec(
    z.string(), z.unknown().pipe(NetworkInterfaceFilterSchema), {
        decode: (text, ctx): unknown => {
            try {
                const parsedValue: unknown = JSON.parse(text);
                return parsedValue;
            } catch (failure) {
                ctx.issues.push({
                    code: 'invalid_format',
                    format: 'json',
                    input: text,
                    message: String(failure),
                });
                return z.NEVER;
            }
        },
        encode: (filter): string => JSON.stringify(filter),
    },
);

/** The media_server's operational settings, as GET and PATCH both answer. */
export const ServerSettingsSchema = z.object({
    // Each declared default is the server's own, so it is what a server leaving the key out
    // means; the filter's is a document, which the empty string is not.
    discoveryEnabled: declareMediaServerDefault(z.boolean(), true),
    useHwDecoder: declareMediaServerDefault(z.boolean(), true),
    autoAdjustCameraSettings: z.boolean(),
    disabledNetworkAddresses: declareMediaServerDefault(NetworkInterfaceFilterCodec, '{}'),
    /** Read-only; the toolbox connects on its own. */
    toolboxConnected: z.boolean(),
});
export type ServerSettings = z.infer<typeof ServerSettingsSchema>;

/** A server's settings as the store holds them; null until they load. */
export type ServerSettingsSnapshot = SettingsSnapshot<ServerSettings>;

/** A server's hardware and software facts as the store holds them; null until they load. */
export interface ServerInfoSnapshot {
    info: ServerInfo | null;
    isLoading: boolean;
    error: Error | null;
}

/** A server's clock as the store holds it; null until it loads. */
export interface ServerTimeSnapshot {
    time: ServerTime | null;
    isLoading: boolean;
    error: Error | null;
}

/** The addresses a server's network interfaces hold; null until they load. */
export interface ServerAddressesSnapshot {
    addresses: ServerLocalAddresses | null;
    isLoading: boolean;
    error: Error | null;
}

/** What a PATCH may change; the server applies the keys it receives. */
export const ServerSettingsPatchSchema = ServerSettingsSchema
    .omit({ toolboxConnected: true })
    .partial();
export type ServerSettingsPatch = z.infer<typeof ServerSettingsPatchSchema>;

/** A recording storage as the cloud mirrors it: its settings, no live state. */
export { StorageSchema };
export type Storage = z.infer<typeof StorageSchema>;

/** How far a storage's index rebuild has got; `idle` once none runs. */
export const RebuildStatusSchema = z.object({
    state: z.enum(['idle', 'counting', 'scanning', 'done', 'cancelled', 'failed']),
    percent: z.number(),
    /** Day folders listed while counting; the scan's percent runs over them. */
    foldersScanned: z.number(),
    filesDone: z.number(),
    cameras: z.number(),
    startedAtMs: z.number(),
    /** 0 until the rebuild ends. */
    finishedAtMs: z.number(),
    /** What the rebuild did, or why it failed. */
    message: z.string(),
});
export type RebuildStatus = z.infer<typeof RebuildStatusSchema>;

/**
 * Why a storage check or change failed. The server adds codes; one the UI does not act on is
 * shown by its message.
 */
export type StorageErrorCode =
    'none' | 'invalidRequest' | 'invalidUrl' | 'duplicateRoot' | 'unknownRoot' | 'currentRoot' |
    'mountedVolume' | 'unreachable' | 'invalidCredentials' | 'accessDenied' | 'shareNotFound' |
    'hostUnreachable' | 'notSupported' | 'platformError' | 'busy' |
    (string & NonNullable<unknown>);

const StorageErrorCodeSchema = z.custom<StorageErrorCode>(
    (value: unknown): boolean => typeof value === 'string',
);

/** A storage as its server reports it, with the server's last check. */
export const StorageWithStatusSchema = StorageSchema.extend({
    /** Pipe-joined, e.g. "read|write". */
    flags: z.string(),
    status: ResourceStatusSchema,
    error: z.object({
        code: StorageErrorCodeSchema,
        message: z.string(),
    }),
    freeBytes: z.number(),
    totalBytes: z.number(),
    /** The archive's own bytes, once the server has read its logs. */
    usedBytes: z.number().optional(),
    rebuild: RebuildStatusSchema.optional(),
});
export type StorageWithStatus = z.infer<typeof StorageWithStatusSchema>;

/**
 * A storage as the Storages tab holds it: the server's own with its last check, or, while the
 * server is offline, the settings the cloud mirrors.
 */
export type StorageEntry =
    { isLive: true; storage: StorageWithStatus } | { isLive: false; storage: Storage };

/** One server's storages as the store holds them. */
export interface StoragesSnapshot {
    entries: StorageEntry[];
    /** A load has answered; a failed reload keeps the entries it answered with. */
    isLoaded: boolean;
    isLoading: boolean;
    error: Error | null;
}

/** The live list, as every storage change answers it; `storages` is what it holds. */
export const StoragesSchema = listOf(StorageWithStatusSchema, 'storages', {
    getKey: keyBy('id'),
});

/**
 * `Awaiting` until a load is seen in flight after mount, `Pending` while it runs, then its
 * verdict.
 */
export enum GateStatus {
    Awaiting = 'awaiting',
    Pending = 'pending',
    Has = 'has',
    None = 'none',
    Forbidden = 'forbidden',
    Error = 'error',
}

/** What listServers answered, once a caller has decided. */
export type ServersVerdict =
    GateStatus.Has | GateStatus.None | GateStatus.Forbidden | GateStatus.Error;
